import { FC, useMemo, useState } from "react"
import { DataGrid, SnackbarAlert } from "@/core/components"
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-pro"
import { GRID_PAGE_SIZE_OPTIONS } from "@core/constants"
import {
  useActiveMailingsQuery,
  useFinishMailingMutation,
  useRemoveMailingMutation,
} from "../../store"
import { TgMailing } from "../../interfaces"
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material"
import { Cancel as CancelIcon } from "@mui/icons-material"
import { useAuth } from "@/modules/app/hooks"
import { App } from "@/modules/app/interfaces"
import { hasModuleWriteAccess } from "@/modules/app/utils"

const STATE_CHIP: Partial<
  Record<
    TgMailing.MailingState,
    { label: string; color: "warning" | "info" | "default" }
  >
> = {
  enqueuing: { label: "Загружается", color: "warning" },
  running: { label: "Идёт", color: "info" },
  finishing: { label: "Завершается", color: "default" },
  // finished/cancelled в активных мелькают только в гонке → «—»
}

interface IProps {}
export const ActiveMailings: FC<IProps> = (props) => {
  const auth = useAuth()
  const canManage = useMemo(() => {
    if (!auth) return false
    if (auth.type === App.AuthType.OIDC) {
      const userInfo = auth.userInfo as App.OIDCUserInfo | undefined
      const userRoles = userInfo?.roles ?? []
      return hasModuleWriteAccess(userRoles, App.ModuleName.TG_MAILING)
    }
    // STANDARD: страница рассылок доступна с роли MANAGER и выше.
    return (auth.role ?? App.UserRole.ANON) >= App.UserRole.MANAGER
  }, [auth])

  const { isFetching, data } = useActiveMailingsQuery(
    {},
    { pollingInterval: 30000 },
  )
  const [removeMailing, { isLoading: isRemoving }] = useRemoveMailingMutation()
  const [finishMailing, { isLoading: isFinishing }] = useFinishMailingMutation()

  const isProcessing = isRemoving || isFinishing

  // Рассылка, для которой открыт диалог отмены.
  const [target, setTarget] = useState<TgMailing.ActiveMailing | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    variant: "success" | "error"
    message: string
  }>({ open: false, variant: "success", message: "" })

  // Рассылка считается запущенной, если у неё проставлена дата старта либо уже
  // есть прогресс по сообщениям. Отложенная рассылка ждёт scheduledDate и ещё
  // не стартовала.
  const isMailingStarted = (m: TgMailing.ActiveMailing) =>
    !!m.startedDate || m.completed > 0 || m.failed > 0

  const closeDialog = () => {
    if (isProcessing) return
    setTarget(null)
  }

  const handleConfirm = async () => {
    if (!target) return
    const id = target.id
    // Запущенная → сохраняем отчёт (finnish). Отложенная → просто удаляем.
    const started = isMailingStarted(target)
    try {
      if (started) {
        await finishMailing({ queryParams: { id } }).unwrap()
      } else {
        await removeMailing({ queryParams: { id } }).unwrap()
      }
      setSnackbar({
        open: true,
        variant: "success",
        message: started
          ? "Рассылка остановлена, отчёт сохранён"
          : "Отложенная рассылка удалена",
      })
      setTarget(null)
    } catch (e) {
      setSnackbar({
        open: true,
        variant: "error",
        message: "Не удалось выполнить операцию. Попробуйте ещё раз.",
      })
    }
  }

  const columns = useMemo<GridColDef<TgMailing.ActiveMailing>[]>(
    () => [
      {
        headerName: "ID",
        field: "id",
        flex: 1,
      },
      {
        headerName: "Название",
        field: "name",
        flex: 2,
      },
      {
        headerName: "Состояние",
        field: "state",
        flex: 1,
        renderCell: (params) => {
          const chip = STATE_CHIP[params.row.state as TgMailing.MailingState]
          return chip ? (
            <Chip size="small" label={chip.label} color={chip.color} />
          ) : (
            "—"
          )
        },
      },
      {
        headerName: "В очереди",
        field: "pending",
        flex: 2,
      },
      {
        headerName: "Выполнено",
        field: "completed",
        flex: 2,
      },
      {
        headerName: "Ошибка",
        field: "failed",
        flex: 2,
      },
      {
        headerName: "Дата создания",
        field: "createdAt",
        flex: 2,
      },
      {
        headerName: "Запланирована на",
        field: "scheduledDate",
        flex: 2,
      },
    ],
    [],
  )

  return (
    <>
      <DataGrid<TgMailing.ActiveMailing>
        columns={columns}
        rows={data ?? []}
        loading={isFetching || isProcessing}
        rowCount={data?.length ?? 0}
        //   unstable_headerFilters
        pagination
        paginationMode="client"
        pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
        filterMode="client"
        sortingMode="client"
        rowActions={
          canManage
            ? [
                (params) => (
                  <GridActionsCellItem
                    label="Отменить рассылку"
                    icon={
                      <Tooltip title="Отменить рассылку">
                        <CancelIcon />
                      </Tooltip>
                    }
                    onClick={() => setTarget(params.row)}
                    disabled={isProcessing}
                    color="inherit"
                  />
                ),
              ]
            : undefined
        }
      />

      <Dialog open={!!target} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Отмена рассылки</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {target?.state === "enqueuing" && (
              <>
                Рассылка ещё загружается — отправка уже идёт. «Остановить и
                сохранить отчёт» сохранит частичный отчёт; «Удалить рассылку»
                отменит всё без отчёта.
                <br />
                <br />
              </>
            )}
            {target && isMailingStarted(target) ? (
              <>
                Рассылка{target ? ` «${target.name}»` : ""} уже запущена. Она
                будет остановлена досрочно, а отчёт со статистикой сохранится и
                будет доступен в завершённых рассылках.
                <br />
                <br />
                Сообщения, отправляемые прямо сейчас, успеют доставиться — всё
                неотправленное будет удалено.
              </>
            ) : (
              <>
                Отложенная рассылка{target ? ` «${target.name}»` : ""} ещё не
                запущена. Она будет удалена вместе со всеми данными, отчёт не
                сохраняется.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isProcessing}>
            Отмена
          </Button>
          <Button
            color={target && isMailingStarted(target) ? "warning" : "error"}
            variant="contained"
            onClick={handleConfirm}
            disabled={isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} /> : undefined
            }
          >
            {target && isMailingStarted(target)
              ? "Остановить и сохранить отчёт"
              : "Удалить рассылку"}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarAlert
        isOpen={snackbar.open}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        variant={snackbar.variant}
        message={snackbar.message}
      />
    </>
  )
}
